<template>
    <div ref="root">
        <div v-if="signatureData !== ''">
            <img
                :src="signatureData"
                class="border-1 surface-border h-[175px] w-[350px]"
            />
            <div class="flex gap-2">
                <prime-button
                    severity="secondary"
                    outlined
                    @click="display = true"
                >
                    {{ $t('purchaseContract.documentSection.changeSignature') }}
                </prime-button>

                <inline-message
                    v-if="invalidData"
                    class="js-RequiredFormInput"
                >
                    {{
                        $t(
                            'purchaseContract.documentSection.signatureMissingWarning'
                        )
                    }}
                </inline-message>
            </div>
        </div>

        <div
            v-else
            class="flex gap-2"
        >
            <prime-button @click="display = true">
                {{ $t('purchaseContract.documentSection.clickToSign') }}
            </prime-button>

            <inline-message
                v-if="showErrors"
                class="js-RequiredFormInput"
            >
                {{
                    $t(
                        'purchaseContract.documentSection.signatureRequiredWarning'
                    )
                }}
            </inline-message>
        </div>

        <prime-dialog
            v-model:visible="display"
            :header="$t('purchaseContract.documentSection.signature')"
            :modal="true"
            :pt="{
                root: {
                    class: 'w-full h-full !rounded-none md:h-auto md:!w-[750px]'
                },
                header: {
                    class: '!dark:bg-slate-800 !bg-slate-100 !rounded-none'
                },
                content: {
                    class: '!dark:bg-slate-800 !bg-slate-100 !rounded-none flex-1'
                },
                footer: {
                    class: '!dark:bg-slate-800 !bg-slate-100 !rounded-none'
                }
            }"
            :pt-options="{
                mergeProps: true
            }"
        >
            <app-signature-pad
                canvas-class="h-[350px] w-[700px]"
                :help-text="
                    $t('purchaseContract.documentSection.signatureHelp')
                "
                @data="handleUpdateSignatureData"
            />

            <template #footer>
                <div class="space-x-4">
                    <prime-button
                        :label="$t('cancel')"
                        text
                        @click="handleCancel"
                    />

                    <prime-button
                        :label="$t('ok')"
                        icon="pi pi-check"
                        :disabled="currentData === ''"
                        @click="handleOk"
                    />
                </div>
            </template>
        </prime-dialog>
    </div>
</template>

<script setup lang="ts">
import { nextTick, ref, watch } from 'vue'
import PrimeButton from 'primevue/button'
import PrimeDialog from 'primevue/dialog'
import AppSignaturePad from '~/components/ContentTemplate/AppSignaturePad.vue'
import InlineMessage from 'primevue/inlinemessage'

const props = defineProps({
    signatureData: {
        type: String,
        required: true
    },
    showErrors: {
        type: Boolean,
        required: true
    },
    invalidData: {
        type: Boolean,
        required: true
    }
})

const emit = defineEmits<{
    (e: 'update:signatureData', payload: string): void
}>()
const display = ref(false)
const currentData = ref(props.signatureData)

function handleUpdateSignatureData(data: string) {
    currentData.value = data
}

function handleOk() {
    emit('update:signatureData', currentData.value)
    display.value = false
}

function handleCancel() {
    display.value = false
}

const root = ref<HTMLFormElement>()
watch([() => props.signatureData, display], newValues => {
    if (newValues[0] !== '' && newValues[1] === false) {
        nextTick().then(() => {
            root.value?.scrollIntoView({
                behavior: 'smooth',
                block: 'center'
            })
        })
    }
})
</script>
